import { type GetUsersResponse } from "@mg/schemas/src/christo/catalyst";
import { CheckCircle } from "@phosphor-icons/react";
import { Button, Checkbox, Dialog, Flex, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUI } from "../../../contexts/ui";
import { errorAnalyticsPayload, useAnalytics } from "../../../utils/analytics";
import { errorMessage } from "../../../utils/http";
import { useResetUserReviewerProfile } from "../../../utils/queries/users";

type ResetUser = Pick<GetUsersResponse[number], "_id" | "name" | "role">;

interface ResetCustomReviewDialogProps {
  /** The Manage Users row being reset; null keeps the dialog closed. */
  user: ResetUser | null;
  /** How many Knowledge Database documents the user's custom review links. */
  documentCount: number;
  onClose: () => void;
  /** Fires after christo confirms the reset so the caller can refetch. */
  onReset: () => void;
}

/**
 * Manage Users > reset a person's custom (Personalize) review.
 *
 * Deleting the review is the main action; removing the Knowledge Database
 * documents that review added is a separate opt-in, off by default, because
 * those documents may have become evidence the rest of the enterprise uses.
 */
export function ResetCustomReviewDialog({
  user,
  documentCount,
  onClose,
  onReset,
}: ResetCustomReviewDialogProps) {
  const { t } = useTranslation("ui");
  const { notify } = useUI();
  const posthog = useAnalytics("Users");
  const resetMutation = useResetUserReviewerProfile();
  const [removeDocuments, setRemoveDocuments] = useState(false);

  // Each row opens with the opt-in cleared, never carrying the last choice.
  useEffect(() => {
    setRemoveDocuments(false);
  }, [user?._id]);

  const title = user?.name
    ? t("settings.usersRoute.resetReviewTitle", { name: user.name })
    : t("settings.usersRoute.resetReviewTitleUnnamed");

  const confirm = () => {
    if (!user) return;
    const target = user;
    const withDocuments = removeDocuments && documentCount > 0;
    resetMutation.mutate(
      { userId: target._id, removeDocuments: withDocuments },
      {
        onSuccess: (result) => {
          posthog.capture("user_custom_review_reset", {
            userId: target._id,
            userRole: target.role,
            removeDocuments: withDocuments,
            removedDocuments: result.removedDocuments,
          });
          notify({
            variant: "success",
            title: "Custom review reset",
            message: withDocuments
              ? `Removed ${result.removedDocuments} Knowledge Database document(s) as well.`
              : `${target.name || "This user"} can set up a new one from Personalize.`,
            leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
          });
          onReset();
          onClose();
        },
        onError: async (error) => {
          posthog.capture("user_custom_review_reset_error", {
            userId: target._id,
            userRole: target.role,
            ...errorAnalyticsPayload(error),
          });
          notify({
            variant: "error",
            title: "Failed to reset custom review",
            message: await errorMessage(error),
          });
        },
      },
    );
  };

  return (
    <Dialog.Root
      open={user != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Content
        className="grid w-[90vw] max-w-lg gap-4"
        data-testid="reset-custom-review-dialog"
      >
        <Dialog.Title data-testid="reset-custom-review-dialog-title">
          {title}
        </Dialog.Title>
        <Dialog.Description size="2" color="gray">
          {t("settings.usersRoute.resetReviewDescription")}
        </Dialog.Description>
        {documentCount > 0 && (
          <Text as="label" size="2">
            <Flex gap="2" align="start">
              <Checkbox
                checked={removeDocuments}
                onCheckedChange={(checked) =>
                  setRemoveDocuments(checked === true)
                }
                data-testid="reset-custom-review-remove-documents"
              />
              <Flex direction="column" gap="1">
                <span>
                  {t("settings.usersRoute.resetReviewRemoveDocuments", {
                    count: documentCount,
                  })}
                </span>
                <Text size="1" color="gray">
                  {t("settings.usersRoute.resetReviewRemoveDocumentsHint")}
                </Text>
              </Flex>
            </Flex>
          </Text>
        )}
        <Flex gap="2" justify="end">
          <Dialog.Close>
            <Button variant="outline" data-testid="reset-custom-review-cancel">
              {t("cancel")}
            </Button>
          </Dialog.Close>
          <Button
            color="red"
            disabled={resetMutation.isPending || user == null}
            data-testid="reset-custom-review-confirm"
            onClick={confirm}
          >
            {t("settings.usersRoute.resetReviewConfirm")}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
